// frontend/src/hooks/useToast.ts
import { useState, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Toast notification types
 */
export type ToastType = 'info' | 'success' | 'warning' | 'error';

/**
 * Options for creating a toast
 */
export interface ToastOptions {
  type?: ToastType;
  message?: string;
  title?: string;
  subMessage?: string;
  autoDismiss?: boolean;
  duration?: number;
  [key: string]: unknown;
}

/**
 * Toast notification object
 */
export interface Toast extends ToastOptions {
  id: number;
  type: ToastType;
  autoDismiss: boolean;
  duration: number;
}

/**
 * Return type for the useToast hook
 */
export interface UseToastReturn {
  toasts: Toast[];
  showToast: (options: ToastOptions) => number;
  hideToast: (id: number) => void;
  showSuccess: (message: string, options?: ToastOptions) => number;
  showWarning: (message: string, options?: ToastOptions) => number;
  showError: (message: string, options?: ToastOptions) => number;
  showInfo: (message: string, options?: ToastOptions) => number;
}

// =============================================================================
// HOOK
// =============================================================================

// Toast ids come from a counter. They used to be `Date.now() + Math.random()`,
// which is not unique: at today's epoch-millisecond magnitude (~1.8e12) a double
// keeps only 12 bits of the fraction, so two toasts shown in the same
// millisecond shared an id about 1 time in 4,096, and hideToast removed both.
let nextToastId = 1;

/**
 * Hook for managing toast notifications
 * Supports multiple toasts and different types
 */
export const useToast = (): UseToastReturn => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((options: ToastOptions): number => {
    const id = nextToastId++;
    const toast: Toast = {
      id,
      type: 'info',
      autoDismiss: true,
      duration: 5000,
      ...options
    };

    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  const hideToast = useCallback((id: number): void => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, options: ToastOptions = {}): number => {
    return showToast({
      type: 'success',
      message,
      ...options
    });
  }, [showToast]);

  const showWarning = useCallback((message: string, options: ToastOptions = {}): number => {
    return showToast({
      type: 'warning',
      message,
      ...options
    });
  }, [showToast]);

  const showError = useCallback((message: string, options: ToastOptions = {}): number => {
    return showToast({
      type: 'error',
      message,
      ...options
    });
  }, [showToast]);

  const showInfo = useCallback((message: string, options: ToastOptions = {}): number => {
    return showToast({
      type: 'info',
      message,
      ...options
    });
  }, [showToast]);

  return {
    toasts,
    showToast,
    hideToast,
    showSuccess,
    showWarning,
    showError,
    showInfo
  };
};
